"use client";

import { createContext, useContext } from "react";
import { usePathname } from "next/navigation";
import type { LocaleConfig } from "../config";

export interface LocaleProviders {
  LocaleProvider: (props: {
    prefix: string;
    children: React.ReactNode;
  }) => React.ReactElement;
  /** Returns the active locale prefix in a client component. Defaults to the default prefix. */
  useLocalePrefix: () => string;
  /**
   * Returns the logical (user-facing, unprefixed) pathname for path-matching
   * in client components. usePathname() returns the browser-visible URL, which
   * for non-default locales includes a prefix (e.g. "/ja/view-demos"). This
   * hook strips it so callers can compare against unprefixed routes as before.
   *
   *   // Japanese user on /ja/view-demos
   *   useLogicalPathname() → "/view-demos"
   *   // Default-locale user on /view-demos
   *   useLogicalPathname() → "/view-demos"
   */
  useLogicalPathname: () => string;
}

/**
 * Builds a `LocaleProvider` + hooks pair scoped to the app's own `LocaleConfig`,
 * so `useLogicalPathname` knows which prefix is the unprefixed default.
 */
export function createLocaleProvider(config: LocaleConfig): LocaleProviders {
  const LocalePrefixContext = createContext<string>(config.DEFAULT_PREFIX);

  function LocaleProvider({
    prefix,
    children,
  }: {
    prefix: string;
    children: React.ReactNode;
  }) {
    return (
      <LocalePrefixContext.Provider value={prefix}>
        {children}
      </LocalePrefixContext.Provider>
    );
  }

  function useLocalePrefix(): string {
    return useContext(LocalePrefixContext);
  }

  function useLogicalPathname(): string {
    const pathname = usePathname();
    const prefix = useLocalePrefix();
    if (prefix === config.DEFAULT_PREFIX) return pathname;
    const prefixPath = `/${prefix}`;
    if (pathname.startsWith(`${prefixPath}/`))
      return pathname.slice(prefixPath.length);
    if (pathname === prefixPath) return "/";
    return pathname;
  }

  return { LocaleProvider, useLocalePrefix, useLogicalPathname };
}
